import type { CSSProperties } from 'react';
import { useFormContext } from 'react-hook-form';
import { AlertCircle, CheckCircle2, Info, User } from 'lucide-react';
import SectionCard from '../SectionCard';
import { useRoleEditorContext } from '../RoleEditorContext';
import { SOVEREIGN } from '../../../../epc/utils/epcProjectVisual';
import type { RoleEditorFormValues } from '../types';

/**
 * Step 1 of 6 — Identity.
 *
 * Captures the role's name, optional description and whether it is a
 * mobile-app user (drives whether the Access-type and Approval sections
 * are even rendered downstream).
 */
export default function IdentitySection() {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<RoleEditorFormValues>();

  const { isReadonly, isMobileUser, setIsMobileUser } = useRoleEditorContext();

  const nameValue = watch('name') ?? '';
  const descriptionValue = watch('description') ?? '';
  const nameHasValue = nameValue.trim().length > 0;
  const nameValid = nameHasValue && !errors.name;

  return (
    <SectionCard
      icon={User}
      title="Role identity"
      sub="Name, description and account type for this role."
      step={1}
      totalSteps={6}
    >
      <div className="space-y-5">
        {/* Role name */}
        <div className="space-y-1.5">
          <label
            htmlFor="role-name"
            className="block text-[12px] font-medium"
            style={{ color: SOVEREIGN.ink }}
          >
            Role name <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <User size={14} style={{ color: SOVEREIGN.kpiCaption }} />
            </div>
            <input
              id="role-name"
              type="text"
              disabled={isReadonly}
              {...register('name')}
              placeholder="e.g., Project Head, Department Manager"
              className="h-10 w-full rounded-lg pl-8 pr-9 py-2 text-sm focus:outline-none focus:ring-2 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              style={
                errors.name
                  ? {
                      border: '1px solid #fca5a5',
                      background: '#fff',
                      color: SOVEREIGN.ink,
                      '--tw-ring-color': '#fca5a533',
                    } as React.CSSProperties
                  : nameValid
                    ? {
                        border: `1px solid ${SOVEREIGN.brand}`,
                        background: SOVEREIGN.inputBg,
                        color: SOVEREIGN.ink,
                        '--tw-ring-color': `${SOVEREIGN.brand}33`,
                      } as React.CSSProperties
                    : {
                        border: `1px solid ${SOVEREIGN.inputBorder}`,
                        background: SOVEREIGN.inputBg,
                        color: SOVEREIGN.ink,
                        '--tw-ring-color': `${SOVEREIGN.brand}33`,
                      } as React.CSSProperties
              }
            />
            {nameValid && (
              <CheckCircle2
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: SOVEREIGN.brand }}
              />
            )}
            {errors.name && (
              <AlertCircle className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
            )}
          </div>
          <p
            className="text-[11px]"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Visible to admins assigning users. Keep it short and clear.
          </p>
          {errors.name && (
            <p className="flex items-center gap-1 text-[11px] text-red-600">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {errors.name.message}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label
            htmlFor="role-description"
            className="block text-[12px] font-medium"
            style={{ color: SOVEREIGN.ink }}
          >
            Description{' '}
            <span
              className="text-[10px] font-normal"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              (optional)
            </span>
          </label>
          <textarea
            id="role-description"
            disabled={isReadonly}
            rows={3}
            maxLength={200}
            {...register('description')}
            placeholder="Brief context for admins assigning this role."
            className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 transition-colors resize-none disabled:cursor-not-allowed disabled:opacity-60"
            style={
              errors.description
                ? {
                    border: '1px solid #fca5a5',
                    background: '#fff',
                    color: SOVEREIGN.ink,
                    '--tw-ring-color': '#fca5a533',
                  } as React.CSSProperties
                : {
                    border: `1px solid ${SOVEREIGN.inputBorder}`,
                    background: SOVEREIGN.inputBg,
                    color: SOVEREIGN.ink,
                    '--tw-ring-color': `${SOVEREIGN.brand}33`,
                  } as React.CSSProperties
            }
          />
          <div className="flex items-center justify-between">
            <p
              className="text-[11px]"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              Brief context — what this role is for. Visible on the role list.
            </p>
            <span
              className="text-[11px] tabular-nums"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              {descriptionValue.length} / 200
            </span>
          </div>
          {errors.description && (
            <p className="flex items-center gap-1 text-[11px] text-red-600">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {errors.description.message}
            </p>
          )}
        </div>

        {/* Mobile user toggle */}
        <div
          className="rounded-lg p-4"
          style={{
            border: `1px solid ${SOVEREIGN.cardBorder}`,
            background: SOVEREIGN.inputBg,
          }}
        >
          <div className="flex items-start gap-3">
            <input
              id="isMobileUserRole"
              type="checkbox"
              checked={isMobileUser}
              disabled={isReadonly}
              onChange={(e) => {
                const next = e.target.checked;
                setIsMobileUser(next);
                setValue('isMobileUserRole', next, { shouldDirty: true });
              }}
              className="mt-0.5 w-4 h-4 rounded focus:ring-2 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60"
              style={{
                accentColor: SOVEREIGN.bottle,
                '--tw-ring-color': `${SOVEREIGN.brand}33`,
              } as CSSProperties}
            />
            <div className="flex-1 min-w-0">
              <label
                htmlFor="isMobileUserRole"
                className="block text-sm font-medium cursor-pointer"
                style={{ color: SOVEREIGN.ink }}
              >
                Is mobile app user
              </label>
              <p
                className="text-[12px] mt-0.5 leading-snug"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                If enabled, this role logs in via OTP and does not participate in
                approval chains.
              </p>
              {isMobileUser && (
                <div
                  className="mt-2 flex items-start gap-2 rounded-md px-3 py-2 text-[12px]"
                  style={{
                    background: 'rgba(31,90,71,0.07)',
                    border: `1px solid ${SOVEREIGN.cardBorder}`,
                    color: SOVEREIGN.bottle,
                  }}
                >
                  <Info size={13} className="shrink-0 mt-0.5" />
                  <span>
                    Mobile-app users log in via OTP and skip the approval-chain
                    step.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
